import { useState } from "react";
import { Check, Plus } from "lucide-react";
import {
  STARTER_CATEGORIES, STARTER_HABITS, STARTER_HABITS_ELEMENTARY, STARTER_HABITS_HIGH_SCHOOL,
  OTHER_OPTION, inferFieldFrequency,
} from "./onboardingData";
import { COLLEGE_COURSE_HABITS, WORK_JOB_HABITS, OTHER_FIELD_HABITS } from "./onboardingFieldHabits";
import { CategoryIcon } from "../components/CategoryIcon";
import { vividDark } from "../theme/colorEngine";
import { uid } from "../utils/storage";
import { todayStr } from "../utils/dateHelpers";
import OnboardingScreen from "./OnboardingChrome";
import {
  contentArea, bottomBar, primaryBtn, textBtn, heading,
} from "./onboardingStyles";

const S2_TOTAL = 2; // categories / starter habits — week-start & planning-style pages removed

// School and Work's suggestions all come from onboardingFieldHabits.js (plus
// the generic cat-school/cat-work fallback lists) — ~3,240 per-course/job
// entries, too many to hand-tag one by one like the categories below (which
// carry a hand-picked `freq`/`tod` per suggestion, see FREQ/TOD in
// onboardingData.js). inferFieldFrequency reads the cadence already implied
// by each suggestion's own copy instead — see its comment in onboardingData.js.
const FIELD_DOMAINS = new Set(["cat-school", "cat-work"]);

// Builds a real habit object from a suggestion, matching the shape HabitsTab's
// own add-habit form produces. frequencyType/days/timeOfDay come from the
// suggestion's own `freq`/`tod`, or from inferFieldFrequency for School/Work,
// so the habit starts fully customized — no "Not set" reminder — with
// values the user can still freely change from the habit's edit form.
function buildStarterHabit(suggestion, cat) {
  const inferred = suggestion.freq ? null : (FIELD_DOMAINS.has(cat.id) ? inferFieldFrequency(suggestion.name) : null);
  const freq = suggestion.freq || inferred?.freq;
  const tod = suggestion.tod || inferred?.tod;
  return {
    id: uid(),
    name: suggestion.name,
    emoji: suggestion.emoji,
    phosphorIcon: "",
    emojiLocked: false,
    goalId: cat.goalId || cat.id,
    frequencyType: freq?.frequencyType,
    days: freq?.days || [],
    dayLabels: "named",
    monthDates: [],
    weeklyCount: 3,
    monthlyCount: 10,
    monthlyMode: "dates",
    yearlyMode: "dates",
    yearDates: [],
    yearlyCount: 12,
    color: cat.color,
    dailyCount: 1,
    habitType: "todo",
    limitedOffDays: [],
    maxTimesPerDay: 1,
    quitReason: "",
    quitDate: todayStr(),
    habitStartDate: "",
    endDate: "",
    restrictedTimeStart: "22:00",
    restrictedTimeEnd: "06:00",
    restrictedDays: [],
    restrictedMonthDates: [],
    allowanceMode: "",
    allowanceMax: 2,
    showOnDailyList: undefined,
    description: "",
    addToClockIn: true,
    timeOfDay: tod,
    createdAt: todayStr(),
  };
}

function OnboardingSection2({
  step, setStep, direction, setDirection, anim, setAnim, onBack, answers, setAnswers, onFinish, onEnterFieldFlow,
  setHabits, setCategories,
}) {
  // Categories <-> Starter Habits stays inside this section's own dots —
  // entering/leaving the section entirely (transition screen, Work/School
  // picker) is decided by OnboardingFlow before this component even mounts.
  const goForward = (n) => { setDirection("forward"); setAnim("slide"); setStep(n); };

  const addCategoriesToApp = (ids) => {
    setCategories(prev => {
      const existing = new Set(prev.map(c => c.id));
      const toAdd = STARTER_CATEGORIES.filter(c => ids.includes(c.id) && !existing.has(c.id));
      return toAdd.length ? [...prev, ...toAdd] : prev;
    });
  };

  const screenProps = { direction, anim, progress: step + 1, total: S2_TOTAL, onBack };

  if (step === 0) {
    const handleCategoriesNext = () => {
      if (answers.categories.length === 0) { onFinish(); return; }
      // School asked before Work for a "working student" who picked both —
      // matches the starter-habits screen's tab order below.
      const domains = [];
      if (answers.categories.includes("cat-school")) domains.push("school");
      if (answers.categories.includes("cat-work")) domains.push("work");
      if (domains.length) onEnterFieldFlow(domains);
      else goForward(1);
    };
    return (
      <OnboardingScreen {...screenProps} stepKey="s2-0" footer={
        <div style={bottomBar}>
          <button style={primaryBtn(false)} onClick={handleCategoriesNext}>Next</button>
        </div>
      }>
        <CategoriesStep
          selected={answers.categories}
          onToggle={(id) => {
            // Functional update so rapid taps always accumulate correctly —
            // computing the next array from the `selected` prop instead (as
            // this used to) can race ahead of React's re-render on a slow
            // WebView, letting a later tap overwrite earlier ones instead of
            // adding to them.
            setAnswers(a => ({
              ...a,
              categories: a.categories.includes(id)
                ? a.categories.filter(x => x !== id)
                : [...a.categories, id],
            }));
            addCategoriesToApp([id]); // additive/idempotent either direction
          }}
          onToggleAll={() => {
            setAnswers(a => {
              const allIds = STARTER_CATEGORIES.map(c => c.id);
              const allSelected = allIds.every(id => a.categories.includes(id));
              return { ...a, categories: allSelected ? [] : allIds };
            });
            addCategoriesToApp(STARTER_CATEGORIES.map(c => c.id));
          }}
        />
      </OnboardingScreen>
    );
  }
  return (
    <OnboardingScreen {...screenProps} stepKey="s2-1" footer={
      <div style={bottomBar}>
        <button style={primaryBtn(false)} onClick={onFinish}>Get Started</button>
      </div>
    }>
      <StarterHabitsStep
        selectedCategoryIds={answers.categories}
        answers={answers}
        setHabits={setHabits}
      />
    </OnboardingScreen>
  );
}

// 2-column tile grid (Jade, 2026-10-11; was a one-column list of rows): an
// illustration over the name, soft lavender-grey fill + thin border; a picked tile fills
// with its category's own colour (no check) and pops briefly when
// tapped. Plain page background, no picked count (Jade).
const CAT_TILE_CSS = `
@keyframes obTilePop { 0% { transform: scale(1); } 40% { transform: scale(0.94); } 100% { transform: scale(1); } }
.ob-tile-pop { animation: obTilePop 0.26s cubic-bezier(0.22,1,0.36,1); }
@media (prefers-reduced-motion: reduce) { .ob-tile-pop { animation: none; } }
`;

// Storyset illustrations on the tiles instead of icons (Jade, 2026-10-11;
// credited in Settings > Credits). Backdrop scenery and floor shadow were
// stripped and each viewBox cropped to the drawing so it reads at tile size.
// Categories without one yet keep their icon.
const CAT_ILLUSTRATIONS = {
  "cat-hygiene": "/illustrations/categories/cat-hygiene.svg",
  "cat-fitness": "/illustrations/categories/cat-fitness.svg",
  "cat-home": "/illustrations/categories/cat-home.svg",
  "cat-productivity": "/illustrations/categories/cat-productivity.svg",
  "cat-finance": "/illustrations/categories/cat-finance.svg",
  "cat-work": "/illustrations/categories/cat-work.svg",
  "cat-art": "/illustrations/categories/cat-art.svg",
  "cat-school": "/illustrations/categories/cat-school.svg",
};

function CategoriesStep({ selected, onToggle, onToggleAll }) {
  const allIds = STARTER_CATEGORIES.map(c => c.id);
  const allSelected = allIds.every(id => selected.includes(id));
  // Bumped per tap so the tapped tile's key changes and its pop replays.
  const [popped, setPopped] = useState({ id: null, n: 0 });

  return (
    <div style={contentArea}>
      <style>{CAT_TILE_CSS}</style>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={heading}>Pick a few areas of your life to start with</div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", minHeight: 26 }}>
          <button onClick={onToggleAll} style={{ ...textBtn, color: "var(--c-pink-text, var(--c-primary-deep, #d4789a))", padding: "2px 0" }}>
            {allSelected ? "Deselect all" : "Select all"}
          </button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 4 }}>
          {STARTER_CATEGORIES.map((c, idx) => {
            const active = selected.includes(c.id);
            // An odd last tile (School) spans both columns, laid out sideways.
            const wide = idx === STARTER_CATEGORIES.length - 1 && STARTER_CATEGORIES.length % 2 === 1;
            const ink = vividDark(c.color);
            return (
              <button
                key={popped.id === c.id ? `${c.id}-${popped.n}` : c.id}
                className={popped.id === c.id ? "ob-tile-pop" : undefined}
                onClick={() => { setPopped(p => ({ id: c.id, n: p.n + 1 })); onToggle(c.id); }}
                style={{
                  position: "relative", display: "flex", flexDirection: wide ? "row" : "column", alignItems: "center",
                  justifyContent: "center", gap: wide ? 12 : 6, padding: wide ? "10px 12px" : "12px 8px 10px", minHeight: wide ? 0 : 86,
                  gridColumn: wide ? "1 / -1" : undefined,
                  borderRadius: 20, cursor: "pointer", fontFamily: "inherit",
                  // Picked = the category's own colour, solid (Jade's hex codes in
                  // DEFAULT_CATEGORIES, the same colour the Categories tab uses).
                  background: active ? c.color : "#F7F5FB",
                  // No shadow (Jade, 2026-10-11: coloured shadows were tried and
                  // removed). The card's shape comes from a soft lavender-grey
                  // fill and a thin border instead; a picked card's border
                  // matches its fill so it reads as one solid shape.
                  boxShadow: "none",
                  border: `1.5px solid ${active ? c.color : "#E7E2F0"}`,
                  color: "#000", fontSize: 14.5, fontWeight: 700, lineHeight: 1.25, textAlign: "center",
                }}
              >
                {CAT_ILLUSTRATIONS[c.id] ? (
                  <img src={CAT_ILLUSTRATIONS[c.id]} alt="" decoding="sync" style={{ height: wide ? 44 : 64, maxWidth: "88%", objectFit: "contain", display: "block" }} />
                ) : (
                  <span style={{
                    width: 40, height: 40, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                    background: active ? "rgba(255,255,255,0.75)" : `color-mix(in srgb, ${c.color} 30%, #fff)`,
                  }}>
                    <CategoryIcon cat={c} size={23} color={ink} />
                  </span>
                )}
                {c.title}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Field-specific content (job/course habits) is no longer a separate bonus
// tab — it becomes the actual suggestion list shown under the real "Work" or
// "School" tab, replacing that category's generic STARTER_HABITS list.
// Returns null when there's no field-specific answer yet (no reordering
// score, generic list is used instead — see getSuggestionsForCat below).
function fieldHabitsFor(catId, answers) {
  if (catId === "cat-work") {
    const { workIndustry, workJob, workJobOther } = answers;
    if (!workIndustry || !workJob) return null;
    if (workJob === OTHER_OPTION) return workJobOther ? OTHER_FIELD_HABITS : null;
    return WORK_JOB_HABITS[workIndustry]?.[workJob] || null;
  }
  if (catId === "cat-school") {
    const { educationLevel, collegeField, collegeCourse, collegeCourseOther } = answers;
    if (educationLevel === "elementary") return STARTER_HABITS_ELEMENTARY;
    if (educationLevel === "high-school") return STARTER_HABITS_HIGH_SCHOOL;
    if (educationLevel === "college" && collegeField && collegeCourse) {
      if (collegeCourse === OTHER_OPTION) return collegeCourseOther ? OTHER_FIELD_HABITS : null;
      return COLLEGE_COURSE_HABITS[collegeField]?.[collegeCourse] || null;
    }
    return null;
  }
  return null;
}

// Obstacle-tag scoring: habits tagged with an obstacle the user picked in
// Section 1 sort to the top (stable — equal-score habits keep their
// authored order). No reordering if the user picked "None of these" or
// skipped the obstacle question entirely (see the proposal this implements,
// STARTER_HABITS in onboardingData.js).
function sortByObstacles(habits, obstacles) {
  if (!obstacles || !obstacles.length || obstacles.includes("none")) return habits;
  return habits
    .map((h, i) => ({ h, i, score: (h.tags || []).filter(t => obstacles.includes(t)).length }))
    .sort((a, b) => b.score - a.score || a.i - b.i)
    .map(x => x.h);
}

function getSuggestionsForCat(cat, answers) {
  if (!cat) return [];
  const fieldHabits = fieldHabitsFor(cat.id, answers);
  const habits = fieldHabits || STARTER_HABITS[cat.id] || [];
  return sortByObstacles(habits, answers.obstacles);
}

// School, then Work, then everything else in the usual chip order — so
// whichever category has field-specific suggestions waiting (the one thing
// on this screen actually tailored to the user's own answers) is the first
// tab they see, not buried after generic categories.
const TAB_PRIORITY = { "cat-school": 0, "cat-work": 1 };

function StarterHabitsStep({ selectedCategoryIds, answers, setHabits }) {
  const cats = STARTER_CATEGORIES
    .filter(c => selectedCategoryIds.includes(c.id))
    .sort((a, b) => (TAB_PRIORITY[a.id] ?? 99) - (TAB_PRIORITY[b.id] ?? 99));
  const [openCatId, setOpenCatId] = useState(cats[0]?.id || null);
  // Maps `${catId}::${habitName}` -> the added habit's id, so a second tap
  // on the same "+"/check button can undo it (remove it from the real
  // habit list, not just visually un-tick it).
  const [added, setAdded] = useState(() => new Map());

  const toggleHabit = (cat, suggestion) => {
    const key = `${cat.id}::${suggestion.name}`;
    const existingId = added.get(key);
    if (existingId) {
      setHabits(prev => prev.filter(h => h.id !== existingId));
      setAdded(prev => { const next = new Map(prev); next.delete(key); return next; });
      return;
    }
    const habit = buildStarterHabit(suggestion, cat);
    setHabits(prev => [...prev, habit]);
    setAdded(prev => new Map(prev).set(key, habit.id));
  };

  const openCat = cats.find(c => c.id === openCatId) || cats[0];
  const openSuggestions = getSuggestionsForCat(openCat, answers);

  return (
    <div style={contentArea}>
      <div>
        <div style={heading}>Add a few starter habits</div>
      </div>

      {/* Horizontally scrollable category tabs — one per Space the user
          picked. flexShrink: 0 + minHeight on the row itself guards against
          it collapsing to a sliver on some WebViews when the row's content
          overflows horizontally (seen on-device with the bordered-pill
          version of this tab bar — the row rendered a few px tall with the
          pills' rounded corners barely peeking through). Work/School show
          field-specific suggestions in place of their generic list once that
          answer is known (see getSuggestionsForCat), rather than a separate
          bonus tab. */}
      <style>{`.onboarding-tabs-row::-webkit-scrollbar { display: none; }`}</style>
      <div
        className="onboarding-tabs-row"
        style={{
          display: "flex", flexShrink: 0, minHeight: 32, gap: 14, overflowX: "auto", paddingBottom: 6,
          WebkitOverflowScrolling: "touch", scrollbarWidth: "none", msOverflowStyle: "none",
        }}
      >
        {cats.map((c) => {
          const active = c.id === openCat?.id;
          return (
            <button
              key={c.id}
              onClick={() => setOpenCatId(c.id)}
              style={{
                display: "flex", alignItems: "center", gap: 7, flexShrink: 0,
                background: "none", border: "none", cursor: "pointer", fontFamily: "inherit",
                padding: "6px 2px", fontSize: 15, fontWeight: 700,
                color: active ? "var(--c-pink-text, var(--c-primary-deep, #d4789a))" : "var(--c-text-soft, #999)",
                textDecoration: active ? "underline" : "none",
                textUnderlineOffset: 5,
              }}
            >
              <CategoryIcon cat={c} size={16} /> {c.title}
            </button>
          );
        })}
      </div>

      {/* Suggestion cards for the open category */}
      {openCat && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {openSuggestions.map((s) => {
            const key = `${openCat.id}::${s.name}`;
            const isAdded = added.has(key);
            return (
              <div
                key={key}
                style={{
                  display: "flex", alignItems: "center", gap: 14, padding: "16px 18px",
                  // A picked card gets a clearly visible, thicker periwinkle border.
                  borderRadius: 18,
                  border: isAdded
                    ? "2.5px solid var(--c-accent-strong, var(--c-primary-deep, #d4789a))"
                    : "2px solid var(--c-border-mid, #eee)",
                  background: isAdded ? "var(--c-bg-active, #fdeef4)" : "var(--c-bg-card, #fff)",
                }}
              >
                <span style={{ fontSize: 20 }}>{s.emoji}</span>
                <span style={{ flex: 1, fontSize: 15.5, fontWeight: 700, color: "var(--c-text, #333)" }}>{s.name}</span>
                <button
                  onClick={() => toggleHabit(openCat, s)}
                  aria-label={isAdded ? "Remove habit" : "Add habit"}
                  style={{
                    width: 34, height: 34, borderRadius: "50%", border: "none",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: isAdded ? "var(--c-accent-strong, var(--c-primary-deep, #d4789a))" : "var(--c-bg-soft, #f5eaf0)",
                    color: "var(--c-text, #000)",
                    cursor: "pointer", flexShrink: 0,
                  }}
                >
                  {isAdded ? <Check size={17} strokeWidth={3} /> : <Plus size={17} strokeWidth={3} />}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default OnboardingSection2;
